<template>
  <section id="ai-clips" class="clips">
    <div ref="stageEl" class="clips__stage">
      <img v-if="active" :src="active.poster" alt="" class="clips__wash">
      <div class="clips__veil"/>

      <!-- The same rail the pictures use, for the same reason: each clip keeps
           its own proportions and the row slides so one is centred. Only the
           centred one is a <video>; the rest are their posters, because
           seventeen decoding at once is a video player, not a gallery. -->
      <div ref="railEl" class="rail" :style="{ '--lead-ratio': leadRatio }">
        <figure
          v-for="(clip, index) in aiClips"
          :key="clip.id"
          class="frame"
          :class="{ 'is-active': index === activeIndex }"
          :style="{ '--ratio': ratioOf(clip) }"
        >
          <video
            v-if="index === activeIndex"
            :src="clip.video"
            :poster="clip.poster"
            :width="clip.width"
            :height="clip.height"
            class="frame__media"
            autoplay
            muted
            loop
            playsinline
          />

          <img
            v-else
            :src="clip.poster"
            :alt="clip.title"
            :width="clip.width"
            :height="clip.height"
            loading="lazy"
            class="frame__media"
          >

          <figcaption v-if="index === activeIndex" class="frame__meta">
            <ShareButton
              :id="clip.id"
              kind="clip"
              :title="clip.title"
            />

            <CountBadge v-if="activeViews" :count="activeViews" kind="view" />
          </figcaption>
        </figure>
      </div>

      <footer class="clips__foot">
        <div class="caption">
          <h3 class="caption__title">{{ active?.title }}</h3>
          <span class="caption__count">
            <span class="caption__index">{{ pad(activeIndex + 1) }}</span>
            <span class="caption__total">/ {{ pad(aiClips.length) }}</span>
          </span>
        </div>
      </footer>
    </div>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { gsap } from 'gsap';
import { aiClips } from '~/data/content';

const stageEl = ref(null);
const railEl = ref(null);
const activeIndex = ref(0);

const MOBILE = '(max-width: 768px)';
const isSmall = () => typeof window !== 'undefined' && window.matchMedia(MOBILE).matches;

const active = computed(() => aiClips[activeIndex.value] ?? null);
const pad = (n) => String(n).padStart(2, '0');

// A clip's own proportions, handed to the stylesheet so a frame can be given
// both of its limits and take whichever runs out first.
const ratioOf = (clip) => (clip?.width || 1) / (clip?.height || 1);

const leadRatio = computed(() => {
  const first = aiClips[0];
  return first?.width && first?.height ? first.width / first.height : 0;
});

let offsets = [];
let setX = null;

function measure() {
  const rail = railEl.value;
  if (!rail) return;

  offsets = Array.from(rail.querySelectorAll('.frame')).map(
    (frame) => frame.offsetLeft + frame.offsetWidth / 2
  );

  if (!isSmall()) apply(activeIndex.value);
}

function apply(position) {
  if (!offsets.length || !setX || isSmall()) return;

  const last = offsets.length - 1;
  const clamped = Math.min(Math.max(position, 0), last);
  const i = Math.floor(clamped);
  const f = clamped - i;
  const from = offsets[i] ?? 0;
  const to = offsets[Math.min(i + 1, last)] ?? from;

  setX(-(from + (to - from) * f - (offsets[0] ?? 0)));
}

// The same hold id the images use. Only one of the two is mounted at a time,
// because the tab decides which, so they never both claim it.
useScrollHold('ai-gallery', {
  length: () => Math.max(aiClips.length - 1, 0) * window.innerHeight * 0.62,
  onProgress: (progress) => {
    const position = progress * Math.max(aiClips.length - 1, 0);
    apply(position);

    const next = Math.round(position);
    if (next !== activeIndex.value) activeIndex.value = next;
  },
});

useDwellView('clip', () => active.value?.id);

const { useCounts } = useLiveContent();
const counts = useCounts();
const activeViews = computed(() => Number(counts.value?.clip?.[active.value?.id]) || 0);



function handleResize() {
  measure();
}

onMounted(() => {
  setX = gsap.quickSetter(railEl.value, 'x', 'px');
  measure();
  window.addEventListener('resize', handleResize);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize);
  setX = null;
});
</script>

<style lang="scss" scoped>
.clips,
.clips__stage {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.clips__stage {
  background: #0b0f14;
  // Nothing inside can paint outside it, so a repaint of the page behind
  // does not reach in here.
  contain: paint;
  --frame-h: clamp(38vh, 56vh, 62vh);
  // The other limit. Height alone decides a frame until the poster is wide
  // enough that width runs out first, and then this does.
  --frame-max-w: calc(100vw - 8rem);
}

.clips__wash {
  position: absolute;
  // Blurred small and scaled up rather than blurred across the whole screen.
  //
  // A 34px blur over a phone viewport is on the order of thirty million texel
  // reads each time it rasterises, and it rasterises on any repaint that
  // reaches it. Changing theme animates the root's background colour for
  // 300ms, which is about eighteen such repaints back to back: that is what
  // was freezing the toggle here and nowhere else.
  //
  // An eighth the box at an eighth the radius is visually identical once
  // scaled back, and rasterises a sixty-fourth of the area. The offsets keep
  // the same 15% bleed the old centred scale(1.15) gave, so the blur still has
  // no visible edge: -7.5% to 107.5% on both axes.
  top: -7.5%;
  left: -7.5%;
  width: 12.5%;
  height: 12.5%;
  object-fit: cover;
  transform-origin: 0 0;
  transform: scale(9.2);
  filter: blur(3.7px) saturate(1.15);
  opacity: 0.5;
  // Its own layer, so a repaint anywhere else does not touch the blur at all.
  will-change: transform;
}

.clips__veil {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at 50% 45%, rgba(11, 15, 20, 0.15), rgba(11, 15, 20, 0.82) 78%),
    linear-gradient(to top, rgba(11, 15, 20, 0.92) 0%, rgba(11, 15, 20, 0) 45%);
}

.rail {
  position: absolute;
  top: 0;
  left: 50%;
  // Capped the same way a frame is, or a wide opening clip would be shifted
  // by more than its own width and open off-centre before any script runs.
  margin-left: calc(min(var(--frame-h) * var(--lead-ratio, 0), var(--frame-max-w)) * -0.5);
  height: 100%;
  display: flex;
  align-items: center;
  gap: clamp(1rem, 2.5vw, 2.75rem);
  will-change: transform;
}

.frame {
  position: relative;
  flex: 0 0 auto;
  // The frame is the poster's own shape and takes whichever of its two limits
  // runs out first. A tall limit alone says nothing about width, and a
  // landscape clip at full frame height is wider than a phone: it hangs off
  // both edges, taking the share and view counts anchored to its bottom corner
  // with it.
  aspect-ratio: var(--ratio, 1);
  height: min(var(--frame-h), calc(var(--frame-max-w) / var(--ratio, 1)));
  width: auto;
  margin: 0;
  border-radius: 10px;
  overflow: hidden;
  opacity: 0.32;
  transform: scale(0.88);
  transition: opacity 0.45s ease, transform 0.45s ease;
  // See the gallery: one large shadow, not one per frame in the rail.
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.35);

  &.is-active {
    opacity: 1;
    transform: scale(1);
    box-shadow: 0 24px 60px rgba(0, 0, 0, 0.5);
  }
}

.frame__media {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.frame__meta {
  position: absolute;
  inset: auto 0 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.8rem;
  padding: 1.6rem 0.85rem 0.6rem;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: rgba(255, 255, 255, 0.9);
  background: linear-gradient(to top, rgba(11, 15, 20, 0.72) 0%, rgba(11, 15, 20, 0) 100%);
  pointer-events: none;
}


.clips__foot {
  position: absolute;
  inset: auto clamp(1.25rem, 4vw, 3rem) clamp(1.25rem, 4vh, 2.5rem);
}

.caption {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: clamp(0.75rem, 1.5vw, 1.25rem);
}

.caption__title {
  margin: 0;
  font-size: clamp(1rem, 2vw, 1.35rem);
  color: #fff;
}

.caption__count {
  font-family: 'Orbitron', sans-serif;
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.6);
}

.caption__index { color: #fff; }

@media (max-width: 768px) {
  .clips__stage {
    --frame-h: clamp(30vh, 42vh, 46vh);
    // Nearly the whole screen: no neighbours to leave room for here, only the
    // swipe, and a wide clip needs every pixel of it to be shown whole.
    --frame-max-w: calc(100vw - 2.5rem);
  }

  .clips,
  .clips__stage {
    height: 100vh;
    height: 100svh;
  }

  .rail {
    position: relative;
    left: 0;
    margin-left: 0;
    transform: none !important;
    height: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    scroll-snap-type: x mandatory;
    overscroll-behavior-x: contain;
    scrollbar-width: none;
    padding-inline: 50vw;

    &::-webkit-scrollbar { display: none; }
  }

  .frame {
    scroll-snap-align: center;
    opacity: 0.55;
  }
}

@media (prefers-reduced-motion: reduce) {
  .frame { transition: none; }
}
</style>
